import { Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import Highcharts, { Point } from 'highcharts/highmaps';
import { get, isEmpty, lowerCase, map } from 'lodash';
import numeral from 'numeral';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import { useQueryMapCompetitor } from 'src/services/queries/useQueryCompanyTradeData';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { getNumberOrDefault } from 'src/utils/helpers/number';
import { getStringOrDefault } from 'src/utils/helpers/stringHelpers';

const CompetitorWorldMap = ({ company }: { company: ICompanyDetail }) => {
  const { t } = useTranslation();
  const [fromTime, setFromTime] = useState(dayjs().subtract(2, 'y').format(FORMAT_DATE_TIME.yyyy_mm_dd));
  const [toTime, setToTime] = useState((dayjs().format(FORMAT_DATE_TIME.yyyy_mm_dd) as string) || null);
  const { palette } = useTheme();

  const { data, isFetching } = useQueryMapCompetitor({
    id: company.id,
    query: {
      company_id: company.id,
      start_date: fromTime,
      end_date: toTime,
    },
    enabled: Boolean(company.id) && dayjs(fromTime).isValid() && dayjs(toTime).isValid(),
  });

  const mapData: Array<number | [string, number | null] | null | Highcharts.SeriesMapDataOptions> = useMemo(() => {
    if (!data.data) return [null];

    return map(
      data.data,
      ({ supplier_country_code, suppliers }) =>
        [getStringOrDefault(lowerCase(supplier_country_code)), getNumberOrDefault(suppliers)] as [
          string,
          number | null,
        ],
    ) as Array<number | [string, number | null] | null | Highcharts.SeriesMapDataOptions>;
  }, [data]);
  if (!isFetching && isEmpty(getArrayDefault(mapData))) {
    return <></>;
  }

  return (
    <WhiteBoxContent>
      <Stack mb={3} spacing={20} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Typography variant="h5">
          Where Are Competitors of{' '}
          <Typography variant="h5" color={'secondary.main'} display={'inline'}>
            {getStringOrDefault(company.name)}
          </Typography>{' '}
          Located?
        </Typography>
        <Stack maxWidth={300} direction={'row'} alignItems={'center'} gap={1}>
          <DatePickerWithDefaultIcon
            value={fromTime ? dayjs(fromTime) : null}
            disableFuture
            onChange={(value) => {
              setToTime(null);
              setFromTime(dayjs(value).format(FORMAT_DATE_TIME.yyyy_mm_dd));
            }}
            label={t('trade_data.search_form.input.from')}
            format={FORMAT_DATE_TIME.MONTH_YEAR}
            slotProps={{
              textField: {
                size: 'small',
                required: true,
              },
            }}
            views={['month', 'year']}
          />
          -
          <DatePickerWithDefaultIcon
            minDate={dayjs(fromTime)}
            maxDate={dayjs(fromTime).add(5, 'y')}
            disabled={!dayjs(fromTime).isValid()}
            value={toTime ? dayjs(toTime) : null}
            disableFuture
            format={FORMAT_DATE_TIME.MONTH_YEAR}
            onChange={(value) => setToTime(dayjs(value).format(FORMAT_DATE_TIME.yyyy_mm_dd))}
            label={t('trade_data.search_form.input.trade_to')}
            slotProps={{
              textField: {
                size: 'small',
                required: true,
              },
            }}
            views={['month', 'year']}
          />
        </Stack>
      </Stack>

      <WorldMapChart
        isLoading={isFetching}
        tooltipFormatter={function () {
          const point: Point = this.point;
          const flag = getEmojiFlag(get(point, 'hc-key'));
          return `<div>
            <b>${flag} ${point.name}</b>
            <br/><b style="color: ${palette.text.secondary}">Company </b> <b>${numeral(point.value).format('0,0')}</b>
          </div>`;
        }}
        height={580}
        type={WorldMapOptions.customer}
        data={mapData}
      />
    </WhiteBoxContent>
  );
};

export default CompetitorWorldMap;
